import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { ROOT_FOLDER_PATH } from '@/common/constants';
import { DataRoomClientFile } from '@/common/types';
import { FileInstance } from '@/contexts/overview/dataroom/fileInstanceUtils';
import { useOverview } from '@/pages/overview/common/utils';
import {
  getFilePrimaryPath,
  sortDataRoomFiles,
} from '@/pages/overview/dataroom/content/common/folderUtils';
import { FilesByPath, NewFolderType } from '@/pages/overview/dataroom/content/common/types';
import { ClausesRevisedObject } from '@/pages/overview/financing-rights/common/utils';
import { FileDetails } from '@/pages/pdf-viewer/PdfViewer';
import { trpcReact } from '@/utils/trpc';

export interface Folder {
  id: string;
  key: string;
  type: string;
  name: string;
  displayName: string | null;
  description: string | null;
  parentFolderId: string | null;
  childFolders?: { id: string }[];
  files?: DataRoomClientFile[];
}

interface IDataRoomContext {
  selectedFile?: DataRoomClientFile;
  selectedFileUrl?: string;
  selectedFileOriginalUrl?: string;
  secondSelectedFile?: DataRoomClientFile;
  secondSelectedFileUrl?: string;
  setSelectedFile: (
    fileName: string,
    explicitContext?: { fileId: string; folderPath: string },
  ) => void;
  selectedFileContext?: { fileId: string; folderPath: string };
  setSelectedFileContext: (context: { fileId: string; folderPath: string } | undefined) => void;
  selectedRenameFile?: DataRoomClientFile;
  setSelectedRenameFile: (file: DataRoomClientFile) => void;
  selectedRenameFileContext?: { fileId: string; folderPath: string };
  setSelectedRenameFileContext: (
    context: { fileId: string; folderPath: string } | undefined,
  ) => void;
  selectedFolder?: string;
  setSelectedFolder: (folderName: string) => void;
  updateFolderSelection: (path: string, toRemove: boolean) => void;
  updateAllFolderSelection: () => void;
  setSelectedFoldersArray: React.Dispatch<React.SetStateAction<string[]>>;
  selectedFoldersArray: string[];
  setDraggedContent: (dragContent: string) => void;
  setIsCheckAllActive: React.Dispatch<React.SetStateAction<boolean>>;
  isViewerOpen: boolean;
  setIsViewerOpen: (val: boolean) => void;
  isSpreadsheetViewerOpen: boolean;
  setIsSpreadsheetViewerOpen: (val: boolean) => void;
  shouldFallbackToPdf: boolean;
  setShouldFallbackToPdf: (val: boolean) => void;
  isCheckAllActive: boolean;
  checkedFiles: DataRoomClientFile[];
  setCheckedFiles: React.Dispatch<React.SetStateAction<DataRoomClientFile[]>>;
  checkedFileInstances: FileInstance[];
  setCheckedFileInstances: React.Dispatch<React.SetStateAction<FileInstance[]>>;
  hasCheckedFiles: boolean;
  goToNextDocument: () => void;
  goToPreviousDocument: () => void;
  paths: string[];
  dataRoomFiles: DataRoomClientFile[];
  filteredDataRoomFiles: DataRoomClientFile[];
  setFilteredDataRoomFiles: React.Dispatch<React.SetStateAction<DataRoomClientFile[]>>;
  combinedDocumentArray: DataRoomClientFile[];
  isFolderOpenByPath: Record<string, boolean>;
  setIsFolderOpenByPath: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  isFolderChecked: Record<string, boolean>;
  setIsFolderChecked: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  initialClickIndex: number;
  setInitialClickIndex: (val: number) => void;
  renameFileIndex: number | undefined;
  setRenameFileIndex: (val: number | undefined) => void;
  isFileRenameActive: boolean;
  updateRenameFormStatus: (val: boolean) => void;
  updateSignature: (fileName: string, truthyValue: boolean) => void;
  selectedFileDetails?: FileDetails;
  secondSelectedFileDetails?: FileDetails;
  selectSecondFileByName: (name: string) => void;
  newlyCreatedFolder: NewFolderType;
  setNewlyCreatedFolder: (val: NewFolderType) => void;
  isDualViewerOpen: boolean;
  setIsDualViewerOpen: (val: boolean) => void;
  sidePanelTabIndex: number;
  setSidePanelTabIndex: (val: number) => void;
  isSidePanelOpen: boolean;
  setIsSidePanelOpen: (val: boolean) => void;
  foldersByPaths: Map<string, Folder[]>;
  pathOrganizedDocuments: Map<string, DataRoomClientFile[]>;
  filteredPathOrganizedDocuments: Map<string, DataRoomClientFile[]>;
  missingDocSourceFile: DataRoomClientFile | undefined;
  setMissingDocSourceFile: (fileName: string, highlight: number[]) => void;
}

//TODO we need to break up this file so that it is more readable and maintainable

export const DataRoomContext = createContext<IDataRoomContext | undefined>(undefined);

export function DataRoomContextProvider({ children }: { children: ReactNode }) {
  const {
    matter: {
      id: clientMatterId,
      number: matterNumber,
      client: { number: clientNumber },
      dataRoomFiles: allFiles,
      folders: allFolders,
    },
  } = useOverview();

  const dataRoomFiles = useMemo(() => {
    return allFiles;
  }, [allFiles]);

  const [selectedFile, setSelectedFile] = useState<DataRoomClientFile | undefined>();
  const [selectedFileContext, setSelectedFileContext] = useState<
    { fileId: string; folderPath: string } | undefined
  >();
  const [selectedRenameFileContext, setSelectedRenameFileContext] = useState<
    { fileId: string; folderPath: string } | undefined
  >();
  const [missingDocSourceFile, setMissingDocSourceFile] = useState<DataRoomClientFile | undefined>(
    () => {
      const savedId = localStorage.getItem('missingDocSourceFileId');
      if (savedId) {
        return dataRoomFiles.find((file) => file.id === savedId);
      }
      return undefined;
    },
  );
  const [secondSelectedFile, setSecondSelectedFile] = useState<DataRoomClientFile | undefined>();
  const [selectedRenameFile, setSelectedRenameFile] = useState<DataRoomClientFile | undefined>();
  const [selectedFolder, setSelectedFolder] = useState('');
  const [selectedFoldersArray, setSelectedFoldersArray] = useState<string[]>([]);
  const [newlyCreatedFolder, setNewlyCreatedFolder] = useState<NewFolderType>({
    folderPath: '',
    isFolderCreated: false,
  });
  const [isCheckAllActive, setIsCheckAllActive] = useState(false);
  const [checkedFiles, setCheckedFiles] = useState<DataRoomClientFile[]>([]);
  const [checkedFileInstances, setCheckedFileInstances] = useState<FileInstance[]>([]);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isSpreadsheetViewerOpen, setIsSpreadsheetViewerOpen] = useState(false);
  const [shouldFallbackToPdf, setShouldFallbackToPdf] = useState(false);
  const [initialClickIndex, setInitialClickIndex] = useState<number>(0);
  const [isFileRenameActive, setIsFileRenameActive] = useState<boolean>(false);
  const [renameFileIndex, setRenameFileIndex] = useState<number>();
  const [isDualViewerOpen, setIsDualViewerOpen] = useState(false);
  const [sidePanelTabIndex, setSidePanelTabIndex] = useState(0);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [filteredDataRoomFiles, setFilteredDataRoomFiles] =
    useState<DataRoomClientFile[]>(dataRoomFiles);

  useEffect(() => {
    setFilteredDataRoomFiles(dataRoomFiles);
  }, [dataRoomFiles]);

  const foldersByPaths = useMemo(() => {
    const map = new Map<string, Folder[]>();

    // Helper function to build path from folder tree
    const buildPath = (folder: Folder, currentPath: string = ''): string => {
      const newPath = currentPath ? `${currentPath}/${folder.key}` : '/' + folder.key;
      return newPath;
    };

    // Helper function to process folder tree recursively
    const processFolder = (folder: Folder, currentPath: string = '') => {
      if (folder.type === 'default') {
        const path = buildPath(folder, currentPath);
        const folders = map.get(path) || [];
        folders.push(folder);
        map.set(path, folders);
      }

      if (folder.childFolders) {
        folder.childFolders.forEach((child: { id: string }) => {
          // look up child by id in allFolders
          const childFolder = allFolders.find((f) => f.id === child.id);
          if (childFolder) {
            processFolder(childFolder, buildPath(folder, currentPath));
          }
        });
      }
    };

    const defaultRoot = allFolders.find(
      (folder) => folder.type === 'default' && folder.key === 'root',
    );

    if (defaultRoot) {
      processFolder(defaultRoot);
    }

    return map;
  }, [allFolders]);

  const paths: string[] = useMemo(() => {
    return Array.from(foldersByPaths.keys());
  }, [foldersByPaths]);

  const [isFolderOpenByPath, setIsFolderOpenByPath] = useState({} as Record<string, boolean>);

  const initialFoldersChecked = useMemo(() => {
    return paths.reduce(
      (checkedFolders, path) => {
        checkedFolders[path] = false;
        return checkedFolders;
      },
      {} as Record<string, boolean>,
    );
  }, [paths]);

  const [isFolderChecked, setIsFolderChecked] =
    useState<Record<string, boolean>>(initialFoldersChecked);

  useEffect(() => {
    const isOpenMap = {} as Record<string, boolean>;
    const storedOpenMap = localStorage.getItem(`folderOpenMapRefactor-${clientMatterId}`);
    paths.forEach((path) => {
      if (path === '/root/~trash') {
        isOpenMap[path] = false;
      } else {
        isOpenMap[path] = true;
      }
    });
    if (storedOpenMap) {
      const parsedStoredData: Record<string, boolean> = storedOpenMap
        ? JSON.parse(storedOpenMap)
        : {};
      parsedStoredData[ROOT_FOLDER_PATH] = true;
      setIsFolderOpenByPath(parsedStoredData);
    } else {
      setIsFolderOpenByPath(isOpenMap);
      const storedOpenMapJson = JSON.stringify(isOpenMap);
      localStorage.setItem(`folderOpenMapRefactor-${clientMatterId}`, storedOpenMapJson);
    }
  }, [clientMatterId, paths]);

  const selectedFileUrl: string | undefined = trpcReact.aws.getPresignedGets.useQuery(
    {
      clientMatterId,
      // The query needs an initial value, but it won't be triggered until the selected file is set or changes
      files: [
        {
          name: selectedFile?.name || missingDocSourceFile?.name || '',
          // Download the pdf version of the file
          pdf: true,
        },
      ],
    },
    {
      enabled: !!selectedFile || !!missingDocSourceFile,
    },
  ).data?.[0].url;

  const selectedFileOriginalUrl: string | undefined = trpcReact.aws.getPresignedGets.useQuery(
    {
      clientMatterId,
      files: [
        {
          name: selectedFile?.name || '',
          pdf: false, // Download the original file
        },
      ],
    },
    {
      enabled: !!selectedFile && !!isSpreadsheetViewerOpen,
    },
  ).data?.[0].url;

  const secondSelectedFileUrl: string | undefined = trpcReact.aws.getPresignedGets.useQuery(
    {
      clientMatterId,
      // The query needs an initial value, but it won't be triggered until the selected file is set or changes
      files: [
        {
          name: secondSelectedFile?.name || '',
          // Download the pdf version of the file
          pdf: true,
        },
      ],
    },
    {
      enabled: !!secondSelectedFile,
    },
  ).data?.[0].url;

  //Get all documents organized by paths
  // Note: Files can appear in multiple folders, so the same file may appear in multiple paths
  const pathOrganizedDocuments = useMemo(() => {
    const docs = new Map<string, DataRoomClientFile[]>();
    const mapKeysIterator = foldersByPaths.keys();
    const mapKeysArray = Array.from(mapKeysIterator);

    for (const path of mapKeysArray) {
      const folders = foldersByPaths.get(path);
      const files = folders?.flatMap((folder) => folder.files);
      docs.set(path, files?.filter((file) => file !== undefined) || []);
    }

    return docs;
  }, [foldersByPaths]);

  //Get only documents matching the search query and parent folders
  const filteredPathOrganizedDocuments = useMemo(() => {
    const docs = new Map<string, DataRoomClientFile[]>();
    const mapKeysIterator = foldersByPaths.keys();
    const mapKeysArray = Array.from(mapKeysIterator);

    // First pass: collect all paths that have matching files
    const pathsWithFiles = new Set<string>();
    for (const path of mapKeysArray) {
      const folders = foldersByPaths.get(path);
      const files = folders?.flatMap((folder) => folder.files);
      const filteredFiles =
        files?.filter(
          (file): file is DataRoomClientFile =>
            file !== undefined && filteredDataRoomFiles.some((f) => f.id === file.id),
        ) || [];
      if (filteredFiles.length > 0) {
        docs.set(path, filteredFiles);
        pathsWithFiles.add(path);
      }
    }

    // Second pass: include parent folders of paths with files
    for (const path of mapKeysArray) {
      if (!pathsWithFiles.has(path)) {
        // Check if this path has any subpaths that contain files
        const hasSubpathsWithFiles = mapKeysArray.some(
          (subPath) => subPath !== path && subPath.startsWith(path) && pathsWithFiles.has(subPath),
        );

        if (hasSubpathsWithFiles) {
          // Include empty folder with empty array
          docs.set(path, []);
        }
      }
    }

    return docs;
  }, [foldersByPaths, filteredDataRoomFiles]);

  const sortedDocumentKeys = useMemo(() => {
    return Array.from(pathOrganizedDocuments.keys()).sort((a, b) => {
      // Use the same sorting as OrganizedView to ensure consistent navigation order
      const newA = a.replaceAll('/', ' /');
      const newB = b.replaceAll('/', ' /');
      if (newA < newB) {
        return -1;
      } else if (newA > newB) {
        return 1;
      }
      return 0;
    });
  }, [pathOrganizedDocuments]);

  const combinedDocumentArray = useMemo(() => {
    const sortedDocumentArray: FilesByPath = {};

    sortedDocumentKeys.forEach((key) => {
      const files = pathOrganizedDocuments.get(key) || [];
      sortedDocumentArray[key] = [...files].sort(sortDataRoomFiles);
    });

    return Object.values(sortedDocumentArray).flat();
  }, [pathOrganizedDocuments, sortedDocumentKeys]);

  //TODO onSelectFile is a mess, let's break it up
  const onSelectFile = useCallback(
    (fileName: string, explicitContext?: { fileId: string; folderPath: string }) => {
      if (fileName !== '') {
        const foundFile = dataRoomFiles.find((file) => file.name === fileName);

        // Open all folders that contain this file (for multi-folder files)
        const foldersToOpen: Record<string, boolean> = {};
        for (const [folderPath, fileList] of pathOrganizedDocuments.entries()) {
          const containsFile = fileList.some((file) => file === foundFile);
          if (containsFile && isFolderOpenByPath[folderPath] === false) {
            foldersToOpen[folderPath] = true;
          }
        }

        // Batch folder opening to prevent multiple state updates
        if (Object.keys(foldersToOpen).length > 0) {
          setIsFolderOpenByPath((prev) => ({
            ...prev,
            ...foldersToOpen,
          }));
        }

        setSecondSelectedFile(undefined);
        setSelectedFile(foundFile);
        setSelectedFolder('');

        // Handle file context setting
        if (foundFile) {
          if (explicitContext) {
            // Direct context setting (from clicks)
            setSelectedFileContext(explicitContext);
          } else {
            // Navigation logic - preserve folder context when possible
            setSelectedFileContext((currentContext) => {
              // For navigation, try to preserve folder context
              if (currentContext) {
                const currentFolderFiles =
                  pathOrganizedDocuments.get(currentContext.folderPath) || [];
                const fileExistsInCurrentFolder = currentFolderFiles.some(
                  (f) => f.name === foundFile.name,
                );

                if (fileExistsInCurrentFolder) {
                  // Update only the file ID, keep the existing folder context
                  return {
                    fileId: foundFile.id,
                    folderPath: currentContext.folderPath,
                  };
                }
              }

              // File doesn't exist in current folder or no context - use primary path
              const primaryPath = getFilePrimaryPath(foundFile, foldersByPaths);
              if (primaryPath) {
                const lastSlashIndex = primaryPath.lastIndexOf('/');
                const folderPath = primaryPath.substring(0, lastSlashIndex);
                return { fileId: foundFile.id, folderPath };
              }

              return currentContext;
            });
          }
        }
      } else {
        setSelectedFile(undefined);
        setSelectedFileContext(undefined);
      }
    },
    [dataRoomFiles, pathOrganizedDocuments, foldersByPaths, isFolderOpenByPath],
  );

  //MISSING DOCUMENTS select source file and save to local storage
  const onSelectMissingDocSourceFile = useCallback(
    (fileId: string, highlight: number[]) => {
      const foundFile = dataRoomFiles.find((file) => file.id === fileId);
      if (foundFile) {
        setMissingDocSourceFile(foundFile);
        localStorage.setItem('missingDocSourceFileId', foundFile.id);
        localStorage.setItem('missingDocSourceHighlight', JSON.stringify(highlight));
      }
    },
    [dataRoomFiles, setMissingDocSourceFile],
  );

  const setDraggedContent = useCallback((dragContent: string) => {
    if (dragContent === '') {
      setSelectedFolder('');
      return;
    }
    const lastFolder = dragContent.lastIndexOf('/');
    const folderName = dragContent.substring(lastFolder + 1);
    setSelectedFolder(folderName);
  }, []);

  const goToNextDocument = useCallback(() => {
    if (!selectedFile || !selectedFileContext) {
      // No current file - select first file in first folder
      for (const folderPath of sortedDocumentKeys) {
        const files = pathOrganizedDocuments.get(folderPath) || [];
        const sortedFiles = [...files].sort(sortDataRoomFiles);
        if (sortedFiles.length > 0) {
          const firstFile = sortedFiles[0];
          localStorage.setItem('current-page', '0');
          localStorage.setItem('document-list-file-id', firstFile.id);
          onSelectFile(firstFile.name, { fileId: firstFile.id, folderPath });
          return;
        }
      }
      return;
    }

    localStorage.setItem('current-page', '0');
    const targetFolderPath = selectedFileContext.folderPath;
    let currentFolderIndex = -1;
    let currentFileIndex = -1;

    // Find current file position within its folder context
    for (let folderIdx = 0; folderIdx < sortedDocumentKeys.length; folderIdx++) {
      const folderPath = sortedDocumentKeys[folderIdx];
      if (folderPath === targetFolderPath) {
        currentFolderIndex = folderIdx;
        const files = pathOrganizedDocuments.get(folderPath) || [];
        const sortedFiles = [...files].sort(sortDataRoomFiles);

        for (let fileIdx = 0; fileIdx < sortedFiles.length; fileIdx++) {
          if (sortedFiles[fileIdx].id === selectedFile.id) {
            currentFileIndex = fileIdx;
            break;
          }
        }
        break;
      }
    }

    if (currentFolderIndex === -1 || currentFileIndex === -1) {
      // Fallback - file not found in expected context, start from beginning
      for (const folderPath of sortedDocumentKeys) {
        const files = pathOrganizedDocuments.get(folderPath) || [];
        const sortedFiles = [...files].sort(sortDataRoomFiles);
        if (sortedFiles.length > 0) {
          const firstFile = sortedFiles[0];
          localStorage.setItem('document-list-file-id', firstFile.id);
          onSelectFile(firstFile.name, { fileId: firstFile.id, folderPath });
          return;
        }
      }
      return;
    }

    // Try next file in current folder
    const currentFolderFiles = pathOrganizedDocuments.get(targetFolderPath) || [];
    const currentSortedFiles = [...currentFolderFiles].sort(sortDataRoomFiles);

    if (currentFileIndex + 1 < currentSortedFiles.length) {
      // Next file in same folder
      const nextFile = currentSortedFiles[currentFileIndex + 1];
      localStorage.setItem('document-list-file-id', nextFile.id);
      onSelectFile(nextFile.name, { fileId: nextFile.id, folderPath: targetFolderPath });
    } else {
      // Move to first file of next folder (or wrap to first folder)
      let nextFolderIndex = (currentFolderIndex + 1) % sortedDocumentKeys.length;

      // Find next folder with files
      let attempts = 0;
      while (attempts < sortedDocumentKeys.length) {
        const nextFolderPath = sortedDocumentKeys[nextFolderIndex];
        const nextFolderFiles = pathOrganizedDocuments.get(nextFolderPath) || [];
        const nextSortedFiles = [...nextFolderFiles].sort(sortDataRoomFiles);

        if (nextSortedFiles.length > 0) {
          const nextFile = nextSortedFiles[0];
          localStorage.setItem('document-list-file-id', nextFile.id);
          onSelectFile(nextFile.name, { fileId: nextFile.id, folderPath: nextFolderPath });
          return;
        }

        nextFolderIndex = (nextFolderIndex + 1) % sortedDocumentKeys.length;
        attempts++;
      }
    }
  }, [selectedFile, selectedFileContext, sortedDocumentKeys, pathOrganizedDocuments, onSelectFile]);

  const goToPreviousDocument = useCallback(() => {
    if (!selectedFile || !selectedFileContext) {
      // No current file - select last file in last folder
      for (let i = sortedDocumentKeys.length - 1; i >= 0; i--) {
        const folderPath = sortedDocumentKeys[i];
        const files = pathOrganizedDocuments.get(folderPath) || [];
        const sortedFiles = [...files].sort(sortDataRoomFiles);
        if (sortedFiles.length > 0) {
          const lastFile = sortedFiles[sortedFiles.length - 1];
          localStorage.setItem('current-page', '0');
          localStorage.setItem('document-list-file-id', lastFile.id);
          onSelectFile(lastFile.name, { fileId: lastFile.id, folderPath });
          return;
        }
      }
      return;
    }

    localStorage.setItem('current-page', '0');
    const targetFolderPath = selectedFileContext.folderPath;
    let currentFolderIndex = -1;
    let currentFileIndex = -1;

    // Find current file position within its folder context
    for (let folderIdx = 0; folderIdx < sortedDocumentKeys.length; folderIdx++) {
      const folderPath = sortedDocumentKeys[folderIdx];
      if (folderPath === targetFolderPath) {
        currentFolderIndex = folderIdx;
        const files = pathOrganizedDocuments.get(folderPath) || [];
        const sortedFiles = [...files].sort(sortDataRoomFiles);

        for (let fileIdx = 0; fileIdx < sortedFiles.length; fileIdx++) {
          if (sortedFiles[fileIdx].id === selectedFile.id) {
            currentFileIndex = fileIdx;
            break;
          }
        }
        break;
      }
    }

    if (currentFolderIndex === -1 || currentFileIndex === -1) {
      // Fallback - file not found in expected context, start from end
      for (let i = sortedDocumentKeys.length - 1; i >= 0; i--) {
        const folderPath = sortedDocumentKeys[i];
        const files = pathOrganizedDocuments.get(folderPath) || [];
        const sortedFiles = [...files].sort(sortDataRoomFiles);
        if (sortedFiles.length > 0) {
          const lastFile = sortedFiles[sortedFiles.length - 1];
          localStorage.setItem('document-list-file-id', lastFile.id);
          onSelectFile(lastFile.name, { fileId: lastFile.id, folderPath });
          return;
        }
      }
      return;
    }

    // Try previous file in current folder
    const currentFolderFiles = pathOrganizedDocuments.get(targetFolderPath) || [];
    const currentSortedFiles = [...currentFolderFiles].sort(sortDataRoomFiles);

    if (currentFileIndex - 1 >= 0) {
      // Previous file in same folder
      const prevFile = currentSortedFiles[currentFileIndex - 1];
      localStorage.setItem('document-list-file-id', prevFile.id);
      onSelectFile(prevFile.name, { fileId: prevFile.id, folderPath: targetFolderPath });
    } else {
      // Move to last file of previous folder (or wrap to last folder)
      let prevFolderIndex = currentFolderIndex - 1;
      if (prevFolderIndex < 0) prevFolderIndex = sortedDocumentKeys.length - 1;

      // Find previous folder with files
      let attempts = 0;
      while (attempts < sortedDocumentKeys.length) {
        const prevFolderPath = sortedDocumentKeys[prevFolderIndex];
        const prevFolderFiles = pathOrganizedDocuments.get(prevFolderPath) || [];
        const prevSortedFiles = [...prevFolderFiles].sort(sortDataRoomFiles);

        if (prevSortedFiles.length > 0) {
          const prevFile = prevSortedFiles[prevSortedFiles.length - 1]; // Last file in folder
          localStorage.setItem('document-list-file-id', prevFile.id);
          onSelectFile(prevFile.name, { fileId: prevFile.id, folderPath: prevFolderPath });
          return;
        }

        prevFolderIndex = prevFolderIndex - 1;
        if (prevFolderIndex < 0) prevFolderIndex = sortedDocumentKeys.length - 1;
        attempts++;
      }
    }
  }, [selectedFile, selectedFileContext, sortedDocumentKeys, pathOrganizedDocuments, onSelectFile]);

  const hasCheckedFiles = useMemo(() => {
    return checkedFiles.length > 0;
  }, [checkedFiles.length]);

  const updateFolderSelection = useCallback((path: string, toRemove: boolean) => {
    setSelectedFoldersArray((prevArray) => {
      if (path === '/') return [...prevArray];
      const isSelected = prevArray.includes(path);
      if (toRemove && isSelected) {
        const newArray = prevArray.filter((item) => item !== path);
        if (newArray.length === 0) {
          setSelectedFolder('');
        }
        return newArray;
      } else if (!toRemove && !isSelected) {
        return [...prevArray, path];
      }
      return prevArray;
    });
  }, []);

  const updateAllFolderSelection = useCallback(() => {
    selectedFoldersArray.forEach((folder) => {
      updateFolderSelection(folder, true);
    });
  }, [selectedFoldersArray, updateFolderSelection]);

  const signatureToUpdate = trpcReact.dataRoom.updateMissingSignature.useMutation();
  const updateSignature = useCallback(
    (fileName: string, signatureStatus: boolean) => {
      if (checkedFiles.length > 0) {
        checkedFiles.forEach((file) => {
          signatureToUpdate.mutate({
            clientNumber: clientNumber,
            clientMatterNumber: matterNumber,
            fileName: file.name,
            missingSignature: signatureStatus,
            clientMatterId: clientMatterId,
          });
        });
      }

      signatureToUpdate.mutate({
        clientNumber: clientNumber,
        clientMatterNumber: matterNumber,
        fileName: fileName,
        missingSignature: signatureStatus,
        clientMatterId: clientMatterId,
      });
    },
    [checkedFiles, clientMatterId, clientNumber, matterNumber, signatureToUpdate],
  );

  const updateRenameFormStatus = useCallback((newValue: boolean) => {
    setIsFileRenameActive(newValue);
  }, []);

  const parseFileDetails = useCallback((file: DataRoomClientFile) => {
    return {
      title: file?.displayName || '',
      type: file.docTitle || '',
      effective_date: file?.date?.toString() || 'N/A',
      createdAt: file.createdAt.toString(),
      originalPathname: file.originalPathname,
      summary: file?.summary || '',
      signatories: file.signatories || [],
      missingSignature: file.missingSignature ?? null,
      duplicateCluster: file.duplicateCluster || '',
      id: file.id,
      vetoData: {
        source: {
          displayName: file?.displayName || '',
          name: file?.name || '',
        },
        clausesFound: file.vetoData[0]?.clausesFound || '',
        clausesCulled: file.vetoData[0]?.clausesCulled || '',
        clausesRevised: file.vetoData[0]?.clausesRevised as ClausesRevisedObject,
        id: file.vetoData[0]?.id || '',
      },
      references: file.mentions.map((mention) => {
        return {
          displayName: mention.targetDocument?.displayName || mention.title,
          name: mention.targetDocument?.name || mention.title,
          missing: mention.status === 'Missing',
          highlight: mention.locations,
          generatedTitle: mention.title,
        };
      }),
      referenced_by: file.mentionedBy.map((mention) => {
        return {
          displayName: mention.sourceDocument?.displayName || mention.title,
          name: mention.sourceDocument?.name || mention.title,
          missing: mention.status === 'Missing',
          highlight: mention.locations,
          generatedTitle: mention.title,
        };
      }),
    };
  }, []);

  const selectedFileDetails = useMemo(() => {
    if (!selectedFile) {
      return undefined;
    }
    const newFileRef = dataRoomFiles.find((file) => file.name === selectedFile.name);
    if (!newFileRef) {
      return undefined;
    }
    return parseFileDetails(newFileRef);
  }, [dataRoomFiles, parseFileDetails, selectedFile]);

  const secondSelectedFileDetails = useMemo(() => {
    if (!secondSelectedFile) {
      return undefined;
    }
    return parseFileDetails(secondSelectedFile);
  }, [parseFileDetails, secondSelectedFile]);

  const selectSecondFileByName = useCallback(
    (name: string) => {
      const chosenFile = dataRoomFiles.find((file) => {
        return file.name === name;
      });
      if (chosenFile) {
        setSecondSelectedFile(chosenFile);
      }
    },
    [dataRoomFiles],
  );

  return (
    <DataRoomContext.Provider
      value={{
        selectedFile,
        selectedFileUrl,
        selectedFileOriginalUrl,
        setSelectedFile: onSelectFile,
        selectedFileContext,
        setSelectedFileContext,
        selectedRenameFile,
        setSelectedRenameFile,
        selectedRenameFileContext,
        setSelectedRenameFileContext,
        selectedFolder,
        setSelectedFolder,
        updateFolderSelection,
        updateAllFolderSelection,
        selectedFoldersArray,
        setSelectedFoldersArray,
        setDraggedContent,
        isCheckAllActive,
        setIsCheckAllActive,
        isViewerOpen,
        setIsViewerOpen,
        isSpreadsheetViewerOpen,
        setIsSpreadsheetViewerOpen,
        shouldFallbackToPdf,
        setShouldFallbackToPdf,
        checkedFiles,
        setCheckedFiles,
        checkedFileInstances,
        setCheckedFileInstances,
        hasCheckedFiles,
        goToNextDocument,
        goToPreviousDocument,
        paths,
        dataRoomFiles,
        combinedDocumentArray,
        isFolderOpenByPath,
        setIsFolderOpenByPath,
        isFolderChecked,
        setIsFolderChecked,
        initialClickIndex,
        setInitialClickIndex,
        renameFileIndex,
        setRenameFileIndex,
        isFileRenameActive,
        updateRenameFormStatus,
        updateSignature,
        selectedFileDetails,
        secondSelectedFileDetails,
        selectSecondFileByName,
        secondSelectedFile,
        secondSelectedFileUrl,
        newlyCreatedFolder,
        setNewlyCreatedFolder,
        isDualViewerOpen,
        setIsDualViewerOpen,
        sidePanelTabIndex,
        setSidePanelTabIndex,
        isSidePanelOpen,
        setIsSidePanelOpen,
        foldersByPaths,
        pathOrganizedDocuments,
        filteredDataRoomFiles,
        setFilteredDataRoomFiles,
        filteredPathOrganizedDocuments,
        missingDocSourceFile,
        setMissingDocSourceFile: onSelectMissingDocSourceFile,
      }}
    >
      {children}
    </DataRoomContext.Provider>
  );
}
